import { Plus, Trash2 } from "lucide-react"

import { Boton } from "@/componentes/ui/Boton"
import { normalizarDecimal } from "@/lib/decimales"

import { cifra } from "./descarga"
import { type Linea, TAMANOS } from "./tipos"

export const LINEA_VACIA: Linea = {
  cantidad: "1",
  descripcion: "Traslado de mercancía contenerizada",
  contenedor: "",
  tamano: "40",
  precio_unitario: "",
  gravado: false,
}

/** Céntimos enteros: la vista previa no suma en coma flotante. */
function centimos(cantidad: string, precio: string): number {
  const c = Number(normalizarDecimal(cantidad) ?? "0")
  const p = Math.round(Number(normalizarDecimal(precio) ?? "0") * 100)
  return Number.isFinite(c * p) ? Math.round(c * p) : 0
}

/**
 * Los renglones del recibo: uno por contenedor o por servicio. El importe que se enseña es
 * una VISTA PREVIA; el definitivo —con el exento y el IVA— lo calcula el servidor al emitir.
 */
export function LineasRecibo({
  lineas,
  onCambiar,
  moneda,
}: {
  lineas: Linea[]
  onCambiar: (l: Linea[]) => void
  moneda: string
}) {
  const poner = (i: number, cambio: Partial<Linea>) =>
    onCambiar(lineas.map((l, j) => (j === i ? { ...l, ...cambio } : l)))
  const total = lineas.reduce((s, l) => s + centimos(l.cantidad, l.precio_unitario), 0)
  const gravado = lineas.some((l) => l.gravado)

  return (
    <div className="space-y-2">
      {lineas.map((l, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: los renglones no tienen id hasta guardarse
        <fieldset key={i} className="tarjeta grid gap-2 p-3 sm:grid-cols-12">
          <legend className="sr-only">Renglón {i + 1}</legend>
          <label className="grid gap-1 text-xs sm:col-span-2">
            Cantidad
            <input
              className="campo tabular-nums"
              inputMode="decimal"
              value={l.cantidad}
              onChange={(e) => poner(i, { cantidad: e.target.value })}
            />
          </label>
          <label className="grid gap-1 text-xs sm:col-span-10">
            Descripción del servicio
            <input
              className="campo"
              value={l.descripcion}
              onChange={(e) => poner(i, { descripcion: e.target.value })}
            />
          </label>
          <label className="grid gap-1 text-xs sm:col-span-4">
            Contenedor
            <input
              className="campo uppercase"
              placeholder="CGMU6571252"
              value={l.contenedor ?? ""}
              onChange={(e) => poner(i, { contenedor: e.target.value.toUpperCase() })}
            />
          </label>
          <label className="grid gap-1 text-xs sm:col-span-3">
            Tamaño
            <select
              className="campo"
              value={l.tamano ?? ""}
              onChange={(e) => poner(i, { tamano: e.target.value || null })}
            >
              <option value="">—</option>
              {Object.entries(TAMANOS).map(([v, t]) => (
                <option key={v} value={v}>
                  {t}
                </option>
              ))}
            </select>
          </label>
          <label className="grid gap-1 text-xs sm:col-span-3">
            Precio unitario ({moneda})
            <input
              className="campo tabular-nums"
              inputMode="decimal"
              value={l.precio_unitario}
              onChange={(e) => poner(i, { precio_unitario: e.target.value })}
            />
          </label>
          <div className="flex items-end justify-between gap-2 sm:col-span-2">
            <label className="flex items-center gap-1 text-xs">
              <input
                type="checkbox"
                checked={l.gravado}
                onChange={(e) => poner(i, { gravado: e.target.checked })}
              />
              IVA
            </label>
            <button
              type="button"
              aria-label={`Quitar el renglón ${i + 1}`}
              className="rounded p-2"
              disabled={lineas.length === 1}
              onClick={() => onCambiar(lineas.filter((_, j) => j !== i))}
              style={{ color: "var(--destructive)" }}
            >
              <Trash2 size={16} aria-hidden={true} />
            </button>
          </div>
        </fieldset>
      ))}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Boton
          tamano="sm"
          onClick={() =>
            onCambiar([...lineas, { ...LINEA_VACIA, ...(lineas.at(-1) ?? {}), contenedor: "" }])
          }
        >
          <Plus size={14} aria-hidden={true} /> Otro renglón
        </Boton>
        <p className="text-sm tabular-nums">
          Subtotal: <strong>{cifra(total / 100)}</strong> {moneda}
          <span className="block text-xs" style={{ color: "var(--fg-muted)" }}>
            {gravado
              ? "Más el IVA de los renglones marcados; el total definitivo sale al emitir."
              : "Exento de IVA. El total definitivo sale al emitir."}
          </span>
        </p>
      </div>
    </div>
  )
}
